import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Volunteering types (matching municipality app structure)
export enum VolunteeringStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  STOPPED = 'stopped'
}

export interface VolunteeringTranslation {
  title: string;
  description: string;
  instructions?: string;
  locationName?: string;
  locationAddress?: string;
}

export interface VolunteeringLocation {
  name: string;
  address: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  googleMapsUrl?: string;
}

export interface VolunteeringActivity {
  id: string;
  municipalityId: string;
  status: VolunteeringStatus;
  translations: Record<string, VolunteeringTranslation>;
  location: VolunteeringLocation;
  startDate: Date;
  endDate: Date;
  startDateDisplay?: string;
  endDateDisplay?: string;
  minParticipants?: number;
  maxParticipants?: number;
  currentRegistrations: number;
  registrationCount: number;
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
  viewCount: number;
}

interface VolunteeringStatistics {
  total: number;
  active: number; // in_progress
  upcoming: number; // scheduled
  completed: number;
  cancelled: number; // stopped
  totalRegistrations: number;
  averageRegistrations: number;
}

interface VolunteeringListResponse {
  items: VolunteeringActivity[];
  total: number;
  statistics: VolunteeringStatistics;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to VolunteeringActivity
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): VolunteeringActivity => {
  const docData = data as Record<string, unknown>;
  
  // Convert translations
  const translationsData = docData.translations as Record<string, { title?: string; description?: string; instructions?: string; locationName?: string; locationAddress?: string }> | undefined;
  const translations: Record<string, VolunteeringTranslation> = {};
  if (translationsData) {
    Object.keys(translationsData).forEach((lang) => {
      const trans = translationsData[lang];
      if (trans) {
        translations[lang] = {
          title: trans.title || '',
          description: trans.description || '',
          instructions: trans.instructions,
          locationName: trans.locationName,
          locationAddress: trans.locationAddress,
        };
      }
    });
  }

  // Convert location
  const locationData = docData.location as Record<string, unknown> | undefined;
  const location: VolunteeringLocation = locationData ? {
    name: (locationData.name as string) || '',
    address: (locationData.address as string) || '',
    coordinates: locationData.coordinates as { lat: number; lng: number } | undefined,
    googleMapsUrl: locationData.googleMapsUrl as string | undefined,
  } : {
    name: '',
    address: '',
  };

  const startDate = toDate(docData.startDate);
  const endDate = toDate(docData.endDate);

  return {
    id,
    municipalityId,
    status: (docData.status as VolunteeringStatus) || VolunteeringStatus.DRAFT,
    translations,
    location,
    startDate,
    endDate,
    startDateDisplay: docData.startDateDisplay as string | undefined,
    endDateDisplay: docData.endDateDisplay as string | undefined,
    minParticipants: docData.minParticipants as number | undefined,
    maxParticipants: docData.maxParticipants as number | undefined,
    currentRegistrations: (docData.currentRegistrations as number) || 0,
    registrationCount: (docData.registrationCount as number) || 0,
    authorId: (docData.authorId as string) || '',
    authorName: (docData.authorName as string) || '',
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    viewCount: (docData.viewCount as number) || 0,
  };
};

export const useMunicipalityVolunteering = (municipalityId: string | null) => {
  const [data, setData] = useState<VolunteeringListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchVolunteering = async () => {
      try {
        setLoading(true);
        setError(null);

        const volunteeringCollection = collection(db, 'municipalities', municipalityId, 'volunteeringActivities');
        const snapshot = await getDocs(query(volunteeringCollection, orderBy('createdAt', 'desc')));

        const items: VolunteeringActivity[] = [];
        let totalRegistrations = 0;
        let activeCount = 0;
        let upcomingCount = 0;
        let completedCount = 0;
        let cancelledCount = 0;

        const now = new Date();

        snapshot.forEach((doc) => {
          const activity = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(activity);

          // Calculate statistics
          totalRegistrations += activity.registrationCount || 0;
          
          // Categorize by status
          if (activity.status === VolunteeringStatus.IN_PROGRESS) {
            activeCount++;
          } else if (activity.status === VolunteeringStatus.SCHEDULED) {
            // Check if it's upcoming (future date)
            if (activity.startDate > now) {
              upcomingCount++;
            }
          } else if (activity.status === VolunteeringStatus.COMPLETED) {
            completedCount++;
          } else if (activity.status === VolunteeringStatus.STOPPED) {
            cancelledCount++;
          }
        });

        const total = items.length;
        const averageRegistrations = total > 0 ? Math.round(totalRegistrations / total) : 0;

        setData({
          items,
          total,
          statistics: {
            total,
            active: activeCount,
            upcoming: upcomingCount,
            completed: completedCount,
            cancelled: cancelledCount,
            totalRegistrations,
            averageRegistrations,
          },
        });
      } catch (err) {
        console.error('Error fetching municipality volunteering:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality volunteering');
      } finally {
        setLoading(false);
      }
    };

    fetchVolunteering();
  }, [municipalityId]);

  return { data, loading, error };
};
